/**
 * Environment Configuration
 *
 * Centralized, type-safe environment variable management.
 * All environment variables are validated at startup and provide helpful error messages.
 *
 * Supports both build-time (import.meta.env) and runtime (window._env_) configuration.
 * Runtime config takes precedence for Docker deployments.
 *
 * Usage:
 *   import { env } from '@/config/env';
 *   console.log(env);
 */

// Extend Window interface to include runtime env
// Set by config.js that is generated at container startup by docker-entrypoint.sh
// Values are pulled into env: EnvironmentConfig object for use in code
declare global {
  interface Window {
    _env_?: Record<string, string>;
  }
}

interface EnvironmentConfig {
  /** Current environment mode (development, production, etc.) */
  MODE: string;

  /** Base URL the app is being served from */
  BASE_URL: string;

  /** Whether the app is running in development mode */
  DEV: boolean;

  /** Whether the app is running in production mode */
  PROD: boolean;
}

/**
 * Gets environment variable value with runtime override support.
 * Checks window._env_ first (runtime), then falls back to import.meta.env (build-time).
 */
function getEnv(key: string): string | undefined {
  // Check runtime config first (from docker-entrypoint.sh)
  if (typeof window !== 'undefined' && window._env_) {
    const runtimeValue = window._env_[key as keyof typeof window._env_];
    if (runtimeValue) return runtimeValue;
  }

  // Fall back to build-time config
  return import.meta.env[key];
}

/**
 * Validates and returns a required environment variable.
 * Throws an error if the variable is not defined.
 *
 * @internal Exported for future use when required variables are needed
 */
export function getRequiredEnv(key: string): string {
  const value = getEnv(key);

  if (!value) {
    throw new Error(
      'Missing required environment variable: ' +
        key +
        '\n' +
        'Please ensure ' +
        key +
        ' is defined.',
    );
  }

  return value;
}

/**
 * Centralized environment configuration object.
 * All environment variables are loaded and validated once at module initialization.
 */
export const env: EnvironmentConfig = {
  // Vite Built-in Variables
  MODE: import.meta.env.MODE,
  BASE_URL: import.meta.env.BASE_URL,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
};

/**
 * Validates the environment configuration at startup.
 * Call this in main.tsx to catch configuration errors early.
 */
export function validateEnvironment(): void {
  // Log configuration in development
  if (env.MODE === 'development') {
    console.debug('Debug Vite environment: ', env);
  }
}

// Freeze the configuration object to prevent runtime modifications
Object.freeze(env);
